import Konva from "konva";
import { useEffect, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import type { IConditionNameLookup, IFeatureTrackProps } from "../features-track/FeatureTrack.js";
import { groupsByProject } from "@/app-utils/entity.ts";
import { useTrackInteraction, useTrackTooltip } from "@/app-utils/hooks/useTrackTooltip.ts";
import { normalizedLength } from "@/app-utils/range.ts";
import type { ISelectionModel } from "@/app-utils/selection-model.ts";
import { type ICreateDefaultShapeParams, createFeatureShape } from "@/app-utils/track-shapes/shape-feature.tsx";
import { createMappingShape } from "@/app-utils/track-shapes/shape-mapping.tsx";
import {
  type IFeatureInteraction,
  drawMessageOnTrack,
  getShapeRenderer,
} from "@/app-utils/track-shapes/track-shapes-konva-renderers.ts";
import type { Range } from "@/graphql.ts";
import { selectSingleFeature } from "@/redux/modules/genome-browser.ts";
import { getTableDataSlice, sameTableSpec } from "@/redux/modules/table-data.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { CANVAS_TRACK_MARGIN } from "@/settings/genome-viewer-settings.ts";
import { FEATURE_DISPLAY_NAME, TRACK_TYPE } from "@/settings/settings.ts";
import type { NonNullableFields } from "@/types";

const useDrawFeatures = (featureTrackProps: IFeatureTrackProps, ref) => {
  const {
    track,
    trackHeight,
    pileup,
    expressionValuesOverflow,
    overflow,
    empty,
    showAllFeatures,
    communityAnnotation,
    communityAnnotationOverflow,
    setTooltipContent,
    featureColor,
    tableQuerySpec,
    trackMessage,
  } = featureTrackProps;

  const dispatch = useDispatch<ReduxDispatch>();
  const range = useSelector<IAppState, NonNullableFields<Range>>(
    (state) => state.genomeBrowser.range as NonNullableFields<Range>,
    shallowEqual,
  );
  const width = useSelector<IAppState, number>((state) => state.genomeBrowser.layout.trackWidth, shallowEqual);
  const height = trackHeight + CANVAS_TRACK_MARGIN * 2;
  const expressionColoringConditionNameLookup = useSelector<IAppState, IConditionNameLookup>(
    (state) =>
      state.genomeBrowser.expressionColoringDatasets[track.trackCustomization?.expressionColoring?.datasetId]
        ?.conditionNamesLookup,
    shallowEqual,
  );
  const selectionModel = useSelector<IAppState, ISelectionModel | undefined>((state) => {
    const tableData = tableQuerySpec ? getTableDataSlice(state.tableData, tableQuerySpec) : undefined;
    return tableData?.selectionModel;
  }, shallowEqual);
  const [messageOnTopMargin, setMessageOnTopMargin] = useState<number>();

  // -- Layers
  // Used to draw shapes
  const layer = useMemo(() => new Konva.Layer({ clearBeforeDraw: true, y: CANVAS_TRACK_MARGIN }), []);
  // Used to draw hover effect
  const interactionLayer = useMemo(
    () => new Konva.Layer({ clearBeforeDraw: true, listening: false, y: CANVAS_TRACK_MARGIN }),
    [],
  );
  // Used to draw above all, like "Loading...", "Zoom-in...", etc.
  const textLayer = useMemo(
    () => new Konva.Layer({ clearBeforeDraw: true, listening: false, y: CANVAS_TRACK_MARGIN }),
    [],
  );

  const callTooltip = useTrackTooltip(layer, setTooltipContent);

  const { renderArrow, renderMapping } = useMemo(() => {
    return getShapeRenderer({
      layer,
      normalize: normalizedLength(width, range.start - 1, range.stop, Math.ceil),
      interactionLayer,
      visibleRange: range,
    });
  }, [layer, width, range]);
  const drawTrackMessage = drawMessageOnTrack(textLayer);
  if (trackMessage) {
    drawTrackMessage(trackMessage, {});
  }

  const interactedFeatureId = useSelector<IAppState, number | null>((state) => {
    const highlightedTrackFeature = state.genomeBrowser.highlightedTrackFeature;
    return tableQuerySpec && sameTableSpec(tableQuerySpec, highlightedTrackFeature.tableQuerySpec)
      ? highlightedTrackFeature.id
      : null;
  }, shallowEqual);
  const [featureShapesMap, setFeatureShapesMap] = useState<Map<number, IFeatureInteraction> | null>(null);
  useTrackInteraction(interactedFeatureId, featureShapesMap);

  // setup
  useEffect(() => {
    // We can have access to the stage from the layer. No need to keep it globally.
    const stage = new Konva.Stage({ container: ref.current, width, height });
    stage.add(layer);
    stage.add(interactionLayer);
    stage.add(textLayer);

    return function cleanup() {
      stage.destroy();
    };
  }, []);

  // Resize canvas
  useEffect(() => {
    const stage = layer.getStage();
    if (stage == undefined) {
      return; // probably no canvas yet
    }

    stage.width(width);
    stage.height(height);
  }, [width, height]);

  useEffect(() => {
    const stage = layer.getStage();
    if (stage == undefined) return;

    // Clean layer.
    layer.destroyChildren();
    textLayer.destroyChildren();
    interactionLayer.destroyChildren();

    // Remove cache and set default position because of drag event
    layer.clearCache();
    layer.x(0);
    textLayer.clearCache();
    textLayer.x(0);
    interactionLayer.clearCache();
    interactionLayer.x(0);

    // reset stage height in case we showed a message on top
    stage.height(height);
    setMessageOnTopMargin(0);

    // see message below ""Zoom in to see expression overlays""
    layer.y(CANVAS_TRACK_MARGIN);
    interactionLayer.y(CANVAS_TRACK_MARGIN);

    const type = track.type;
    const typeString = FEATURE_DISPLAY_NAME[type] ?? type;
    if (overflow) {
      drawTrackMessage(`Zoom in to see ${typeString}s`, { resetHeight: true });
    } else if (empty) {
      drawTrackMessage(`No ${typeString}s available`, { resetHeight: true });
    } else if (expressionValuesOverflow) {
      // in case some more zooming is required to display the expression values
      const { textTopMargin } = drawTrackMessage("Zoom in to see expression overlays", { stickToTop: true });
      const messageTopMargin = textTopMargin + 5;
      layer.y(messageTopMargin + layer.y()); // 5px margin + .y() should be CANVAS_TRACK_MARGIN
      interactionLayer.y(messageTopMargin + interactionLayer.y());
      stage.height(height + messageTopMargin); // make the canvas higher
      setMessageOnTopMargin(messageTopMargin);
    } else if (communityAnnotation && communityAnnotationOverflow) {
      // We draw the message on top of the track
      const { textTopMargin } = drawTrackMessage("Zoom in to see community annotation", { stickToTop: true });
      const messageTopMargin = textTopMargin + 5;
      layer.y(messageTopMargin + layer.y()); // 5px margin + .y() should be CANVAS_TRACK_MARGIN
      interactionLayer.y(messageTopMargin + interactionLayer.y());
      stage.height(height + messageTopMargin); // make the canvas higher
      setMessageOnTopMargin(messageTopMargin);
    }

    // If an overflow happens to be there but the data are still present, then we just clean the tracks and show the message (see above)
    if (!overflow) {
      const featuresShapes = new Map<number, IFeatureInteraction>();

      pileup?.features?.forEach((feature) => {
        // In case of protein_location, transcript_location, etc...
        // we need to rely on the targetId rather than on the id
        const groups = groupsByProject(
          communityAnnotation?.[feature.feature.id] || communityAnnotation?.[feature.feature.targetId],
        );
        const selected = selectionModel ? selectionModel.selectedIds.includes(feature.feature.id) : false;

        const shapeParams: ICreateDefaultShapeParams = {
          feature,
          pileupParams: pileup.pileupParams,
          laneNumber: pileup.expressionLaneNumber ?? -1,
          selected,
          expressionColoringConditionNameLookup,
          callTooltip,
          renderArrow,
          groups,
          featureColor:
            featureColor /*|| track.color not in scope, any anyways this value is using the 0x format which is not supported downstream */,
        };

        switch (feature.feature.type) {
          case TRACK_TYPE.GENE:
          case TRACK_TYPE.NUFEATURE:
          case TRACK_TYPE.AAFEATURE:
          case TRACK_TYPE.MARKER_NUS:
          case TRACK_TYPE.BED:
          case TRACK_TYPE.COORDINATE:
            // eslint-disable-next-line no-case-declarations
            const { interactiveShape: featureShape, doInteract: featureInteraction } = createFeatureShape(shapeParams);
            featuresShapes.set(feature.feature.id, featureInteraction);
            featureShape.on("click", ({ evt }) => {
              dispatch(
                selectSingleFeature([feature.feature.id], tableQuerySpec, !selected, evt.ctrlKey || evt.metaKey),
              );
            });
            break;

          case TRACK_TYPE.TRANSCRIPT_LOCATION:
          case TRACK_TYPE.PROTEIN_LOCATION:
            // eslint-disable-next-line no-case-declarations
            const { interactiveShape: mappingShape, doInteract: mappingInteraction } = createMappingShape({
              ...shapeParams,
              renderMapping,
            });
            featuresShapes.set(feature.feature.id, mappingInteraction);
            mappingShape.on("click", ({ evt }) => {
              dispatch(
                selectSingleFeature([feature.feature.id], tableQuerySpec, !selected, evt.ctrlKey || evt.metaKey),
              );
            });
            break;

          default:
            throw new Error("Feature type " + feature.feature.type + " is not supported as a Genomic Feature shape.");
        }
      });
      setFeatureShapesMap(featuresShapes);
    }

    // TODO layer.cache({ x: -width, y: 0, width: width * 3, height });
    layer.batchDraw();
    textLayer.batchDraw();
    interactionLayer.batchDraw();
  }, [
    pileup,
    expressionValuesOverflow,
    overflow,
    empty,
    showAllFeatures,
    range,
    selectionModel,
    communityAnnotation,
    communityAnnotationOverflow,
    track,
    featureColor,
  ]);

  return { messageOnTopMargin };
};

export { useDrawFeatures };
